import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { INSIGHTS, SEO_DEFAULTS } from "@/data/content";

export default function InsightDetail() {
  const { slug } = useParams();
  const insight = useMemo(() => INSIGHTS.find((i) => i.id === slug), [slug]);

  if (!insight) {
    return (
      <div className="pt-40 pb-24 px-6 text-center text-[#3F4A6B]">
        <Seo title="Perspective not found" noindex />
        <div className="font-display text-3xl mb-4">Perspective not found.</div>
        <Link to="/insights" className="text-gold underline">Return to insights</Link>
      </div>
    );
  }

  const related = INSIGHTS.filter((i) => i.id !== insight.id).slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: insight.title,
    image: insight.image,
    datePublished: insight.date,
    author: { "@type": "Organization", name: insight.author || "AlchamisGroup" },
    publisher: { "@type": "Organization", name: "AlchamisGroup", url: SEO_DEFAULTS.baseUrl },
    description: insight.summary,
  };

  return (
    <div data-testid={`insight-detail-${insight.id}`} className="pt-32 pb-24">
      <Seo
        title={insight.title}
        description={insight.summary}
        path={`/insights/${insight.id}`}
        image={insight.image}
        type="article"
        jsonLd={jsonLd}
      />

      <article className="max-w-[920px] mx-auto px-6 md:px-12">
        <Link to="/insights" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#6B7390] hover:text-gold">
          <ArrowLeft size={14} /> All Insights
        </Link>
        <div className="mt-10">
          <div className="text-[0.65rem] tracking-[0.28em] uppercase text-gold/90">{insight.category} · {insight.readTime} · {insight.date}</div>
          <h1 className="font-display text-5xl sm:text-6xl text-[#001B54] mt-6 leading-[1.04]">{insight.title}</h1>
          <p className="text-[#3F4A6B] mt-4 italic">{insight.summary}</p>
          <div className="text-[0.7rem] tracking-[0.22em] uppercase text-[#6B7390] mt-4">By {insight.author}</div>
        </div>
        <div className="aspect-[16/9] mt-12 overflow-hidden border border-[#001B54]/10">
          <img src={insight.image} alt={insight.title} className="w-full h-full object-cover" />
        </div>
        <div className="mt-12 space-y-6 text-[#0A1228] leading-relaxed text-base sm:text-lg">
          {insight.body.map((p, i) => (
            <p key={i} className={i === 0 ? "font-display text-2xl text-[#001B54]" : ""}>{p}</p>
          ))}
        </div>
        <div className="hairline my-14" />
        <div className="text-[0.7rem] tracking-[0.22em] uppercase text-[#6B7390] mb-4">Continue reading</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {related.map((r) => (
            <Link key={r.id} to={`/insights/${r.id}`} className="border border-[#001B54]/10 p-6 hover:border-gold/40 transition-colors">
              <div className="text-[0.6rem] tracking-[0.28em] uppercase text-gold mb-2">{r.category}</div>
              <div className="font-display text-2xl text-[#001B54]">{r.title}</div>
            </Link>
          ))}
        </div>
        <div className="mt-16 text-center">
          <Link to="/contact" className="luxury-button">Speak with a Partner <ArrowRight size={14} /></Link>
        </div>
      </article>
    </div>
  );
}
